/**
 * Copyright Mántum All Rights Reserved.
 */
import Internal from '../internal/input';
import { ACTION_VALUE, useGroup } from '../../group';
import { PropTypes, getValue } from '../internal/datetime/prop-types';
import { formatter, validator } from '@mantum/kanela';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { language } from '../../../language';

import type { FormatTime as Format } from '@mantum/kanela';

const KEY_TEMPLATE = 'HH:mm:ss';

type Value = {
    date: Date | null;
    value: string;
};

export default forwardRef<HTMLInputElement, PropTypes<Format>>(
    function Component(props, ref) {
        // prettier-ignore
        const {
            format = 'HH:mm',
            name
        } = props

        const changeRef = useRef(props.onChange);

        const locale = props.advanced?.locale ?? language();
        const value = getValue(formatter.time, format, props.value);

        const groups = useGroup();
        const [currentValue, setCurrentValue] = useState<Value>(value);

        useEffect(() => {
            const current = getValue(formatter.time, format, props.value);
            setCurrentValue(current);

            if (changeRef.current) {
                if (current.value === '') {
                    changeRef.current(name, '');
                    return;
                }

                changeRef.current(
                    name,
                    formatter.time.format(current.date, KEY_TEMPLATE)
                );
            }
        }, [name, format, props.value]);

        const setValue = (date?: Date | null) => {
            if (date) {
                const value = formatter.time.format(date, format);
                setCurrentValue(() => ({
                    date,
                    value: value ? value : ''
                }));

                if (changeRef.current) {
                    if (value === '') {
                        changeRef.current(name, '');
                        return;
                    }

                    changeRef.current(
                        name,
                        formatter.time.format(date, KEY_TEMPLATE)
                    );
                }
            }
        };

        const handleChange = (_?: string, value?: unknown) => {
            if (typeof value === 'string') {
                setCurrentValue((previous) => ({
                    ...previous,
                    value
                }));
            }
        };

        const onBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
            const chrono = (await import('./library')).default;

            let target = null;
            const value = event.target.value;

            if (value) {
                target = chrono[locale].parseDate(value);
                if (!target) {
                    const format = formatter.time.format(value, KEY_TEMPLATE);
                    if (format) {
                        target = formatter.time.from(format);
                    }
                }
                setValue(target);
            }

            if (props.onBlur && changeRef.current) {
                if (!target) {
                    changeRef.current(name, '');
                    props.onBlur(event);
                    return;
                }

                const value = formatter.time.format(target, format);
                event.target.value = value ? value : '';

                if (value === '') {
                    changeRef.current(name, '');
                    return;
                }

                changeRef.current(
                    name,
                    formatter.time.format(target, KEY_TEMPLATE)
                );
            }

            props.onBlur && props.onBlur(event);
        };

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            const temporal =
                name.includes('-time') && !(name in groups)
                    ? name.substring(0, name.length - 5)
                    : name;

            if (!(temporal in groups)) {
                return;
            }

            const content = groups[temporal];
            if (content.action === ACTION_VALUE) {
                const next = content.value as string | Date | undefined;
                if (isEmpy(next)) {
                    setCurrentValue({
                        date: null,
                        value: ''
                    });

                    if (changeRef.current) {
                        changeRef.current(temporal, '');
                    }
                    return;
                }

                const current = getValue(formatter.time, format, next);
                setCurrentValue(current);

                if (changeRef.current) {
                    changeRef.current(
                        temporal,
                        formatter.time.format(current.date, KEY_TEMPLATE)
                    );
                }
            }
        }, [name, groups, format]);

        return (
            <Internal
                autocomplete='off'
                disabled={props.disabled}
                error={props.error}
                name={props.name}
                onBlur={onBlur}
                onChange={handleChange}
                placeholder={props.placeholder}
                ref={ref}
                testid={props.testid}
                type='text'
                value={currentValue.value}
            />
        );
    }
);

const isEmpy = (value?: string | Date) =>
    validator.isNull(value) ||
    (typeof value === 'string' && validator.isEmpty(value));
